import { Component, type ErrorInfo, type ReactNode } from 'react';

import { captureException } from '../lib/sentry.js';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Forward the error + React component stack to Sentry so on-call gets the
    // full context; keep a console log for devs who aren't running Sentry.
    void captureException(error);
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught:', error, info.componentStack);
  }

  override render() {
    if (this.state.error) {
      return (
        this.props.fallback ?? (
          <div className="error-boundary">
            <h2>Something went wrong</h2>
            <p>{this.state.error.message}</p>
            <button onClick={() => window.location.assign('/dashboard')}>Reload</button>
          </div>
        )
      );
    }
    return this.props.children;
  }
}
